import { Capacitor } from "@capacitor/core";

// Lot 699 — « Repos en arrière-plan ».
// Android tue un processus resté en arrière-plan qui dépasse ~2 % de CPU (ApplicationExitInfo
// REASON_EXCESSIVE_RESOURCE_USAGE « excessive cpu … », vu en production sur la 2.4.1, page /home) : nos minuteurs de
// rafraîchissement (cloche, messages, contacts, battement, aperçus vidéo des stories, bandeaux réseau…) tournaient toujours
// quand l'app était cachée. Ici : UNE source de vérité « l'app est-elle visible ? » (onglet web masqué OU app native en
// arrière-plan via Capacitor `appStateChange`) et `pausableInterval`, un setInterval qui se tait quand l'app est cachée et
// rattrape immédiatement au retour. Les médias (vocaux, musique, mini-lecteur), les pings WebSocket, les lives et les
// enregistrements ne passent PAS par ici : ils gardent leurs minuteurs.
let nativeActive = true;
let installed = false;
const listeners = new Set();

export const isAppVisible = () => nativeActive && (typeof document === "undefined" || document.visibilityState !== "hidden");

const emit = () => {
  const visible = isAppVisible();
  listeners.forEach((fn) => { try { fn(visible); } catch { /* noop */ } });
};

export const installAppVisibility = () => {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("visibilitychange", emit);
  if (Capacitor.isNativePlatform()) {
    // Android : `visibilitychange` n'est pas garanti quand l'activité passe derrière → l'état Capacitor fait foi aussi.
    import("@capacitor/app")
      .then(({ App }) => App.addListener("appStateChange", ({ isActive }) => { nativeActive = !!isActive; emit(); }))
      .catch(() => {});
  }
};

// Abonnement aux transitions visible ↔ cachée (dédoublonnées : un « focus » sans changement n'appelle rien).
export const onAppVisibility = (fn) => {
  installAppVisibility();
  let last = isAppVisible();
  const wrapped = (visible) => { if (visible === last) return; last = visible; fn(visible); };
  listeners.add(wrapped);
  return () => listeners.delete(wrapped);
};

// setInterval qui ne tourne que l'app visible. `onResume` (défaut) : au retour, `fn` est appelée tout de suite
// (rafraîchissement) puis le rythme reprend. Renvoie la fonction d'arrêt (à mettre dans le cleanup de l'effet).
export const pausableInterval = (fn, ms, { onResume = true } = {}) => {
  let id = null;
  const start = () => { if (id == null) id = setInterval(fn, ms); };
  const stop = () => { if (id != null) { clearInterval(id); id = null; } };
  if (isAppVisible()) start();
  const off = onAppVisibility((visible) => {
    if (visible) { if (onResume) { try { fn(); } catch { /* noop */ } } start(); } else stop();
  });
  return () => { stop(); off(); };
};

// Tests uniquement : simule l'état natif (appStateChange).
export const __setNativeActive = (v) => { nativeActive = !!v; emit(); };
